import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Flag } from 'lucide-react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useReadOnly } from '../../contexts/ReadOnlyContext';
import { PageLoader, Pagination } from '../../components/common';
import FlagDetailModal from '../../components/expert/FlagDetailModal';
import { EmptyState, ErrorBanner, PAGE_SIZE, PageHeader, Tabs } from '../../components/expert/ui';
import {
  AdminFlag,
  FLAG_REASON_LABELS,
  FlagTab,
  expertAgentApi,
  expertKeys,
  formatDate,
  getExpertErrorMessage,
  isRequestCancelled,
} from '../../lib/domain-expert-api';

export default function FlaggedAnswersPage() {
  const { hasExpert } = useWorkspace();
  const { isReadOnly } = useReadOnly();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<FlagTab>('open');
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const params = { status: tab, skip: (page - 1) * PAGE_SIZE, limit: PAGE_SIZE };
  const { data, isInitialLoading, isError, error } = useQuery({
    queryKey: expertKeys.flags(params),
    queryFn: () => expertAgentApi.listFlags(params),
    enabled: hasExpert,
    keepPreviousData: true,
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => expertAgentApi.reopenFlag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expert', 'flags'] });
      queryClient.invalidateQueries({ queryKey: expertKeys.flagCounts() });
      toast.success('Moved back to Needs review.');
    },
    onError: (err) => {
      if (isRequestCancelled(err)) return;
      toast.error(getExpertErrorMessage(err));
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const changeTab = (next: FlagTab) => {
    setTab(next);
    setPage(1);
  };

  const expertLabel = (flag: AdminFlag) => flag.flagged_by?.name || flag.flagged_by?.email || '—';

  if (isInitialLoading) return <PageLoader fullScreen={false} />;

  return (
    <div>
      <PageHeader title="Flagged answers" description="Answers your users marked as wrong or incomplete." />

      <div className="mt-6">
        <Tabs
          value={tab}
          onChange={changeTab}
          tabs={[
            { value: 'open', label: 'Needs review', count: data?.counts?.open },
            { value: 'done', label: 'Done', count: data?.counts?.done },
          ]}
        />
      </div>

      {isError && <ErrorBanner message={getExpertErrorMessage(error, "We couldn't load flagged answers.")} />}

      <div className="mt-4 overflow-x-auto bg-white shadow ring-1 ring-black ring-opacity-5">
        {items.length === 0 ? (
          tab === 'open' ? (
            <EmptyState icon={<Flag className="h-12 w-12" />} title="Nothing to review.">
              Flagged answers from your users will show up here.
            </EmptyState>
          ) : (
            <EmptyState title="Nothing here yet.">Answers you fix or close will show up here.</EmptyState>
          )
        ) : (
          <table className="w-full divide-y divide-gray-300">
            <thead className="bg-gray-50">
              <tr>
                {['Question', 'Reason', 'User', 'Date'].map((heading) => (
                  <th key={heading} scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    {heading}
                  </th>
                ))}
                {tab === 'done' && (
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    Outcome
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {items.map((flag) => (
                <tr key={flag.id} className="cursor-pointer hover:bg-gray-50" onClick={() => setSelectedId(flag.id)}>
                  <td className="max-w-md px-6 py-4 text-sm">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedId(flag.id);
                      }}
                      className="line-clamp-2 text-left font-medium text-gray-900 hover:text-indigo-700"
                    >
                      {flag.question}
                    </button>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{FLAG_REASON_LABELS[flag.reason_category] ?? 'Other'}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">{expertLabel(flag)}</td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">{formatDate(flag.created_at)}</td>
                  {tab === 'done' && (
                    <td className="whitespace-nowrap px-6 py-4 text-sm">
                      <span className="text-gray-700">{flag.status === 'resolved' ? 'Fixed' : 'No change needed'}</span>
                      {!isReadOnly && (
                        <button
                          type="button"
                          disabled={reopenMutation.isLoading}
                          onClick={(e) => {
                            e.stopPropagation();
                            reopenMutation.mutate(flag.id);
                          }}
                          className="ml-3 text-xs font-medium text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
                        >
                          Reopen
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > PAGE_SIZE && (
        <Pagination
          className="mt-4"
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          rangeStart={(page - 1) * PAGE_SIZE + 1}
          rangeEnd={Math.min(page * PAGE_SIZE, total)}
          totalItems={total}
          itemLabel="flagged answers"
        />
      )}

      <FlagDetailModal flagId={selectedId} onClose={() => setSelectedId(null)} enabled={hasExpert} readOnly={isReadOnly} />
    </div>
  );
}
